/**
 * CancellationPolicyBuilder
 * Lets vendors define a tiered cancellation / refund policy
 * or opt for "No Cancellation".
 *
 * Stores to formData.cancellationPolicyV2 = {
 *   type: "tiered" | "none",
 *   tiers: [{ id, window, refundPercent }]  // only when type === "tiered"
 * }
 */
import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Plus, Trash2, ShieldCheck, XCircle, Info } from 'lucide-react';

const WINDOW_OPTIONS = [
  { value: '2hrs',   label: '2 Hours before' },
  { value: '4hrs',   label: '4 Hours before' },
  { value: '12hrs',  label: '12 Hours before' },
  { value: '24hrs',  label: '24 Hours before' },
  { value: '48hrs',  label: '48 Hours before' },
  { value: '3days',  label: '3 Days before' },
  { value: '5days',  label: '5 Days before' },
  { value: '7days',  label: '7 Days before' },
  { value: '15days', label: '15 Days before' },
  { value: '30days', label: '30 Days before' },
];

const DEFAULT_TIERS = [
  { id: 't1', window: '30days', refundPercent: 100 },
  { id: 't2', window: '15days', refundPercent: 75 },
  { id: 't3', window: '7days',  refundPercent: 50 },
];

const uid = () => Math.random().toString(36).slice(2, 8);

function windowLabel(w) {
  return WINDOW_OPTIONS.find(o => o.value === w)?.label?.replace(' before', '') || w;
}

export default function CancellationPolicyBuilder({ formData, handleChange }) {
  const policy = formData?.cancellationPolicyV2 || { type: 'tiered', tiers: DEFAULT_TIERS };
  const [localPolicy, setLocalPolicy] = useState(policy);

  const update = useCallback((next) => {
    setLocalPolicy(next);
    handleChange('cancellationPolicyV2', next);
    // Keep legacy field in sync
    handleChange('cancellationAllowed', next.type === 'tiered');
  }, [handleChange]);

  const setType = (type) => {
    const next = type === 'tiered'
      ? { type: 'tiered', tiers: localPolicy.tiers?.length ? localPolicy.tiers : DEFAULT_TIERS }
      : { type: 'none', tiers: [] };
    update(next);
  };

  const addTier = () => {
    const next = {
      ...localPolicy,
      tiers: [...(localPolicy.tiers || []), { id: uid(), window: '7days', refundPercent: 50 }],
    };
    update(next);
  };

  const removeTier = (id) => {
    update({ ...localPolicy, tiers: localPolicy.tiers.filter(t => t.id !== id) });
  };

  const updateTier = (id, field, value) => {
    update({
      ...localPolicy,
      tiers: localPolicy.tiers.map(t => t.id === id ? { ...t, [field]: value } : t),
    });
  };

  const policyType = localPolicy.type || 'tiered';
  const tiers = localPolicy.tiers || [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-blue-600" />
          Cancellation Policy
        </CardTitle>
        <CardDescription>Define how refunds work when customers cancel a booking</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Policy type toggle */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setType('tiered')}
            data-testid="cancellation-type-tiered"
            className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
              policyType === 'tiered'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            Allow Cancellation
          </button>
          <button
            type="button"
            onClick={() => setType('none')}
            data-testid="cancellation-type-none"
            className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
              policyType === 'none'
                ? 'border-red-400 bg-red-50 text-red-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <XCircle className="h-4 w-4" />
            No Cancellation
          </button>
        </div>

        {/* No cancellation message */}
        {policyType === 'none' && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Customers cannot cancel once a booking is confirmed. All payments are non-refundable.</span>
          </div>
        )}

        {/* Tiered policy */}
        {policyType === 'tiered' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600 font-medium">Refund tiers (applied before rental date)</p>
              <span className="text-xs text-gray-400">— add multiple windows for a progressive policy</span>
            </div>

            {tiers.length === 0 && (
              <div className="text-sm text-gray-500 italic py-2">No tiers added. Click "Add Tier" to start.</div>
            )}

            {tiers.map((tier, idx) => (
              <div
                key={tier.id}
                data-testid={`cancellation-tier-${idx}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <span className="text-xs text-gray-500 w-4 shrink-0">{idx + 1}.</span>

                {/* Window */}
                <div className="flex-1">
                  <Select
                    value={tier.window}
                    onValueChange={(v) => updateTier(tier.id, 'window', v)}
                  >
                    <SelectTrigger
                      className="h-8 text-sm"
                      data-testid={`tier-${idx}-window`}
                    >
                      <SelectValue placeholder="Cancel window" />
                    </SelectTrigger>
                    <SelectContent>
                      {WINDOW_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Arrow */}
                <span className="text-gray-400 text-sm shrink-0">→</span>

                {/* Refund % */}
                <div className="flex items-center gap-1 w-28 shrink-0">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={tier.refundPercent}
                    onChange={(e) => updateTier(tier.id, 'refundPercent', Math.min(100, Math.max(0, Number(e.target.value))))}
                    className="h-8 text-sm text-center w-16"
                    data-testid={`tier-${idx}-refund`}
                  />
                  <span className="text-sm text-gray-500 shrink-0">% refund</span>
                </div>

                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeTier(tier.id)}
                  data-testid={`tier-${idx}-remove`}
                  className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addTier}
              data-testid="add-cancellation-tier"
              className="w-full border-dashed"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Tier
            </Button>

            {/* Summary preview */}
            {tiers.length > 0 && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs font-medium text-blue-700 mb-1">Policy Summary:</p>
                <ul className="text-xs text-blue-600 space-y-0.5">
                  {[...tiers]
                    .sort((a, b) => {
                      const order = ['30days','15days','7days','5days','3days','48hrs','24hrs','12hrs','4hrs','2hrs'];
                      return order.indexOf(a.window) - order.indexOf(b.window);
                    })
                    .map((t, i) => (
                      <li key={t.id}>
                        {i === 0 ? `Cancel ${windowLabel(t.window)} or more before → ` : `Cancel ${windowLabel(t.window)} before → `}
                        <strong>
                          {t.refundPercent === 100 ? 'Full refund' :
                           t.refundPercent === 0   ? 'No refund' :
                           `${t.refundPercent}% refund`}
                        </strong>
                      </li>
                    ))
                  }
                  <li className="text-blue-500 italic">After last window → No refund</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
